import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { toast } from 'sonner';
import { formatDistanceToNow, format } from 'date-fns';
import {
  Crown, Search, RefreshCw, ChevronDown, ChevronRight,
  Brain, Sparkles, CheckCircle2, AlertCircle, Clock, Loader2,
  FileText, Image, Video, Link2, ExternalLink, MoreVertical,
  Building2, User, Package, Briefcase, Target, BarChart3,
  Eye, Zap, Bot, Play, Pause, Edit, Trash2, Rocket, Plus,
  GitBranch, Users, Download, FileSpreadsheet, File, Presentation, Paperclip
} from 'lucide-react';
import api from '../api';
import AIInsightsPanel from '../components/AIInsightsPanel';
import AICoachingPanel from '../components/AICoachingPanel';

// Status badge configurations
const STATUS_CONFIG = {
  draft: { color: 'bg-slate-100 text-slate-700 border-slate-200', icon: FileText },
  pending: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock },
  in_progress: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Play },
  blocked: { color: 'bg-red-100 text-red-700 border-red-200', icon: Pause },
  completed: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2 },
};

// Brain model configurations
const BRAIN_CONFIG = {
  left_brain: { name: 'Left Brain', subtitle: 'Analytical', color: 'text-blue-600', bgColor: 'bg-blue-50', model: 'GPT-4o' },
  right_brain: { name: 'Right Brain', subtitle: 'Creative', color: 'text-purple-600', bgColor: 'bg-purple-50', model: 'Claude Sonnet 4.5' },
  cortex: { name: 'Cortex', subtitle: 'Synthesis', color: 'text-emerald-600', bgColor: 'bg-emerald-50', model: 'Gemini 3 Pro' },
};

// Helper functions
const getStatusBadge = (status) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const Icon = config.icon;
  return (
    <Badge variant="outline" className={`${config.color} gap-1`}>
      <Icon className="h-3 w-3" />
      {status.replace('_', ' ')}
    </Badge>
  );
};

const getPriorityBadge = (priority) => {
  const colors = {
    low: 'bg-slate-100 text-slate-600',
    medium: 'bg-blue-100 text-blue-600',
    high: 'bg-orange-100 text-orange-600',
    critical: 'bg-red-100 text-red-600',
  };
  return <Badge variant="outline" className={colors[priority] || colors.medium}>{priority}</Badge>;
};

// AI Reasoning Section Component
const AIReasoningSection = ({ reasoning, expandedReasoning, toggleReasoning }) => {
  if (!reasoning) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Brain className="h-12 w-12 mx-auto mb-3 opacity-30" />
        <p>No AI reasoning data available yet.</p>
        <p className="text-sm">Execute this task to see the 3-brain reasoning process.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Left Brain */}
      <Collapsible open={expandedReasoning.left_brain} onOpenChange={() => toggleReasoning('left_brain')}>
        <CollapsibleTrigger className="w-full">
          <div className={`flex items-center justify-between p-3 rounded-lg ${BRAIN_CONFIG.left_brain.bgColor} hover:opacity-90 transition-opacity`}>
            <div className="flex items-center gap-3">
              <Brain className={`h-5 w-5 ${BRAIN_CONFIG.left_brain.color}`} />
              <div className="text-left">
                <span className={`font-medium ${BRAIN_CONFIG.left_brain.color}`}>{BRAIN_CONFIG.left_brain.name}</span>
                <span className="text-xs ml-2 text-muted-foreground">({BRAIN_CONFIG.left_brain.subtitle})</span>
              </div>
              <Badge variant="outline" className="text-xs">{reasoning.left_brain?.model || BRAIN_CONFIG.left_brain.model}</Badge>
            </div>
            {expandedReasoning.left_brain ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border max-h-[300px] overflow-y-auto">
            <pre className="whitespace-pre-wrap text-sm font-mono">{reasoning.left_brain?.output || 'No output'}</pre>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Right Brain */}
      <Collapsible open={expandedReasoning.right_brain} onOpenChange={() => toggleReasoning('right_brain')}>
        <CollapsibleTrigger className="w-full">
          <div className={`flex items-center justify-between p-3 rounded-lg ${BRAIN_CONFIG.right_brain.bgColor} hover:opacity-90 transition-opacity`}>
            <div className="flex items-center gap-3">
              <Sparkles className={`h-5 w-5 ${BRAIN_CONFIG.right_brain.color}`} />
              <div className="text-left">
                <span className={`font-medium ${BRAIN_CONFIG.right_brain.color}`}>{BRAIN_CONFIG.right_brain.name}</span>
                <span className="text-xs ml-2 text-muted-foreground">({BRAIN_CONFIG.right_brain.subtitle})</span>
              </div>
              <Badge variant="outline" className="text-xs">{reasoning.right_brain?.model || BRAIN_CONFIG.right_brain.model}</Badge>
            </div>
            {expandedReasoning.right_brain ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border max-h-[300px] overflow-y-auto">
            <pre className="whitespace-pre-wrap text-sm font-mono">{reasoning.right_brain?.output || 'No output'}</pre>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Cortex */}
      <Collapsible open={expandedReasoning.cortex} onOpenChange={() => toggleReasoning('cortex')}>
        <CollapsibleTrigger className="w-full">
          <div className={`flex items-center justify-between p-3 rounded-lg ${BRAIN_CONFIG.cortex.bgColor} hover:opacity-90 transition-opacity`}>
            <div className="flex items-center gap-3">
              <Zap className={`h-5 w-5 ${BRAIN_CONFIG.cortex.color}`} />
              <div className="text-left">
                <span className={`font-medium ${BRAIN_CONFIG.cortex.color}`}>{BRAIN_CONFIG.cortex.name}</span>
                <span className="text-xs ml-2 text-muted-foreground">({BRAIN_CONFIG.cortex.subtitle})</span>
              </div>
              <Badge variant="outline" className="text-xs">{reasoning.cortex?.model || BRAIN_CONFIG.cortex.model}</Badge>
            </div>
            {expandedReasoning.cortex ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border max-h-[300px] overflow-y-auto">
            <pre className="whitespace-pre-wrap text-sm font-mono">{reasoning.cortex?.output || 'No output'}</pre>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Assigned Agents */}
      {reasoning.assigned_agents && reasoning.assigned_agents.length > 0 && (
        <div className="mt-4">
          <h4 className="font-medium mb-2 flex items-center gap-2">
            <Bot className="h-4 w-4" />
            Assigned AI Agents
          </h4>
          <div className="space-y-2">
            {reasoning.assigned_agents.map((agent, idx) => (
              <div key={idx} className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium">{agent.name}</span>
                  {agent.role_title && <Badge variant="outline" className="text-xs">{agent.role_title}</Badge>}
                </div>
                {agent.reasoning && (
                  <p className="text-sm text-muted-foreground">{agent.reasoning}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Execution Pathway */}
      {reasoning.execution_pathway && reasoning.execution_pathway.length > 0 && (
        <div className="mt-4">
          <h4 className="font-medium mb-2 flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Execution Pathway
          </h4>
          <div className="space-y-2">
            {reasoning.execution_pathway.map((step, idx) => (
              <div key={idx} className="flex items-start gap-3 p-2 bg-slate-50 dark:bg-slate-900 rounded">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-sm font-medium">
                  {idx + 1}
                </span>
                <div>
                  <p className="text-sm">{step.description || step}</p>
                  {step.agent && <Badge variant="outline" className="text-xs mt-1">{step.agent}</Badge>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Task Card Component
const TaskCard = ({ task, onView, onEdit, onDelete, onExecute, onLink, onFollowUp, onAssignAgent, onForceRestart }) => {
  const hasDeliverables = task.deliverables && task.deliverables.length > 0;
  const hasReasoning = task.ai_reasoning && Object.keys(task.ai_reasoning).length > 0;
  const linkedCount = (task.linked_campaign_ids?.length || 0) + 
                     (task.linked_market_research_ids?.length || 0) + 
                     (task.related_contact_ids?.length || 0) +
                     (task.related_company_ids?.length || 0) +
                     (task.related_deal_ids?.length || 0);
  const assignedAgentsCount = task.assigned_ai_agents?.length || 0;
  const canExecute = task.status === 'pending' || task.status === 'draft';

  return (
    <Card 
      className="hover:shadow-md transition-shadow border-l-4"
      style={{ borderLeftColor: task.status === 'completed' ? '#10b981' : task.status === 'blocked' ? '#ef4444' : '#8b5cf6' }}
      data-testid={`task-card-${task.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onView(task)}>
            <h3 className="font-medium truncate">{task.title}</h3>
            <p className="text-sm text-muted-foreground line-clamp-2">{task.description}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
            {getStatusBadge(task.status)}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onView(task)}>
                  <Eye className="h-4 w-4 mr-2" />
                  View Details
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onEdit(task)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Task
                </DropdownMenuItem>
                {canExecute && (
                  <DropdownMenuItem onClick={() => onExecute(task)}>
                    <Rocket className="h-4 w-4 mr-2" />
                    Execute with AI
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onFollowUp(task)}>
                  <GitBranch className="h-4 w-4 mr-2" />
                  Create Follow-up
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAssignAgent(task)}>
                  <Users className="h-4 w-4 mr-2" />
                  Assign AI Staff
                </DropdownMenuItem>
                {(task.status === 'processing' || task.status === 'blocked' || task.status === 'failed' || task.status === 'paused') && (
                  <DropdownMenuItem onClick={() => onForceRestart(task)}>
                    <RefreshCw className="h-4 w-4 mr-2 text-orange-600" />
                    Force Restart
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => onLink(task)}>
                  <Link2 className="h-4 w-4 mr-2" />
                  Link to Entity
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onDelete(task)} className="text-red-600">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Task
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2 flex-wrap">
            {getPriorityBadge(task.priority)}
            {hasReasoning && (
              <Badge variant="outline" className="bg-violet-50 text-violet-700 gap-1">
                <Brain className="h-3 w-3" />
                AI Reasoning
              </Badge>
            )}
            {hasDeliverables && (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 gap-1">
                <FileText className="h-3 w-3" />
                {task.deliverables.length}
              </Badge>
            )}
            {linkedCount > 0 && (
              <Badge variant="outline" className="bg-blue-50 text-blue-700 gap-1">
                <Link2 className="h-3 w-3" />
                {linkedCount}
              </Badge>
            )}
            {assignedAgentsCount > 0 && (
              <Badge variant="outline" className="bg-indigo-50 text-indigo-700 gap-1">
                <Users className="h-3 w-3" />
                {assignedAgentsCount} AI
              </Badge>
            )}
          </div>
          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(task.created_at), { addSuffix: true })}
          </span>
        </div>

        {/* Quick Actions */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t">
          <Button 
            size="sm" 
            variant="ghost" 
            className="h-7 text-xs"
            onClick={() => onView(task)}
          >
            <Eye className="h-3 w-3 mr-1" />
            View
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            className="h-7 text-xs"
            onClick={() => onEdit(task)}
          >
            <Edit className="h-3 w-3 mr-1" />
            Edit
          </Button>
          {canExecute && (
            <Button 
              size="sm" 
              variant="default" 
              className="h-7 text-xs bg-violet-600 hover:bg-violet-700"
              onClick={() => onExecute(task)}
            >
              <Rocket className="h-3 w-3 mr-1" />
              Execute
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Linked Entities Section Component
const LinkedEntitiesSection = ({ task, onLink }) => {
  const links = [
    { type: 'Campaigns', ids: task.linked_campaign_ids, icon: Target },
    { type: 'Market Research', ids: task.linked_market_research_ids, icon: Search },
    { type: 'Contacts', ids: task.related_contact_ids, icon: User },
    { type: 'Companies', ids: task.related_company_ids, icon: Building2 },
    { type: 'Deals', ids: task.related_deal_ids, icon: Briefcase },
    { type: 'Leads', ids: task.linked_lead_ids, icon: Target },
  ].filter(l => l.ids && l.ids.length > 0);

  if (links.length === 0) {
    return (
      <div className="text-center py-4 text-muted-foreground">
        <Link2 className="h-8 w-8 mx-auto mb-2 opacity-30" />
        <p className="text-sm">No linked entities</p>
        <Button 
          variant="outline" 
          size="sm" 
          className="mt-2"
          onClick={() => onLink(task)}
        >
          <Link2 className="h-3 w-3 mr-1" />
          Link to Entity
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {links.map((link, idx) => {
        const Icon = link.icon;
        return (
          <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-900 rounded">
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">{link.type}</span>
              <Badge variant="outline">{link.ids.length}</Badge>
            </div>
            <Button variant="ghost" size="sm" className="h-7">
              <ExternalLink className="h-3 w-3" />
            </Button>
          </div>
        );
      })}
    </div>
  );
};

export default function LeadershipAIHubPage() {
  const [loading, setLoading] = useState(true);
  const [hubData, setHubData] = useState({ tasks: [], stats: {}, agents: [] });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [filterCategory, setFilterCategory] = useState('');  // Category filter for auto-routing
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [expandedReasoning, setExpandedReasoning] = useState({});
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkEntityType, setLinkEntityType] = useState('');
  const [linkEntityId, setLinkEntityId] = useState('');
  const [linkingTask, setLinkingTask] = useState(null);
  
  // Edit dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    context: '',
    priority: 'medium',
    status: 'pending'
  });
  const [saving, setSaving] = useState(false);
  
  // Delete dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingTask, setDeletingTask] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Execute dialog state
  const [showExecuteDialog, setShowExecuteDialog] = useState(false);
  const [executingTask, setExecutingTask] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [executionProgress, setExecutionProgress] = useState('');
  
  // Follow-up dialog state
  const [showFollowUpDialog, setShowFollowUpDialog] = useState(false);
  const [followUpParent, setFollowUpParent] = useState(null);
  const [followUpForm, setFollowUpForm] = useState({ title: '', description: '', priority: 'medium' });
  const [creatingFollowUp, setCreatingFollowUp] = useState(false);
  
  // AI Agent Assignment dialog state
  const [showAgentDialog, setShowAgentDialog] = useState(false);
  const [assigningToTask, setAssigningToTask] = useState(null);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [agentRole, setAgentRole] = useState('contributor');
  const [agentResponsibilities, setAgentResponsibilities] = useState('');
  const [assigningAgent, setAssigningAgent] = useState(false);

  const fetchHubData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      
      const response = await api.get(`/leadership-ai/hub?${params.toString()}`);
      setHubData(response.data);
    } catch (error) {
      console.error('Failed to fetch hub data:', error);
      toast.error('Failed to load Leadership AI Hub data');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter]);

  // Fetch available AI agents on mount
  const fetchAvailableAgents = useCallback(async () => {
    try {
      const response = await api.get('/leadership-ai/available-agents');
      setAvailableAgents(response.data);
    } catch (error) {
      console.error('Failed to fetch available agents:', error);
    }
  }, []);

  useEffect(() => {
    fetchHubData();
    fetchAvailableAgents();
  }, [fetchHubData, fetchAvailableAgents]);

  // Listen for task creation events from AI Insights panel
  useEffect(() => {
    const handleTaskCreated = () => {
      // Refresh the task list when a new task is created from AI suggestions
      fetchHubData();
    };
    
    window.addEventListener('leadership-task-created', handleTaskCreated);
    return () => {
      window.removeEventListener('leadership-task-created', handleTaskCreated);
    };
  }, [fetchHubData]);

  const filteredTasks = hubData.tasks.filter(task => {
    const matchesSearch = !searchQuery || 
      task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !filterCategory || task.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const handleViewTask = (task) => {
    setSelectedTask(task);
    setShowTaskDialog(true);
    setExpandedReasoning({ left_brain: true, right_brain: true, cortex: true });
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setEditForm({
      title: task.title,
      description: task.description || '',
      context: task.context || '',
      priority: task.priority || 'medium',
      status: task.status || 'pending'
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editingTask) return;
    
    try {
      setSaving(true);
      await api.put(`/leadership-tasks/${editingTask.id}`, editForm);
      toast.success('Task updated successfully');
      setShowEditDialog(false);
      setEditingTask(null);
      fetchHubData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTask = (task) => {
    setDeletingTask(task);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deletingTask) return;
    
    try {
      setDeleting(true);
      await api.delete(`/leadership-tasks/${deletingTask.id}`);
      toast.success('Task deleted successfully');
      setShowDeleteDialog(false);
      setDeletingTask(null);
      fetchHubData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    } finally {
      setDeleting(false);
    }
  };

  const handleExecuteTask = (task) => {
    setExecutingTask(task);
    setShowExecuteDialog(true);
    setExecutionProgress('');
  };

  const confirmExecute = async () => {
    if (!executingTask) return;
    
    try {
      setExecuting(true);
      setExecutionProgress('Initiating 3-brain analysis...');
      
      // Call the execute endpoint
      const response = await api.post(`/leadership-tasks/${executingTask.id}/execute`);
      
      setExecutionProgress('Task execution completed!');
      toast.success('Task executed successfully with AI reasoning');
      
      // Close dialog and refresh
      setTimeout(() => {
        setShowExecuteDialog(false);
        setExecutingTask(null);
        setExecutionProgress('');
        fetchHubData();
      }, 1500);
      
    } catch (error) {
      setExecutionProgress('');
      toast.error(error.response?.data?.detail || 'Failed to execute task');
      setExecuting(false);
    }
  };

  const handleLinkTask = (task) => {
    setLinkingTask(task);
    setShowLinkDialog(true);
  };

  const submitLink = async () => {
    if (!linkingTask || !linkEntityType || !linkEntityId) return;
    
    try {
      await api.post(`/leadership-tasks/${linkingTask.id}/link?entity_type=${linkEntityType}&entity_id=${linkEntityId}`);
      toast.success(`Task linked to ${linkEntityType}`);
      setShowLinkDialog(false);
      setLinkEntityType('');
      setLinkEntityId('');
      setLinkingTask(null);
      fetchHubData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link task');
    }
  };

  // Follow-up task handlers
  const handleFollowUp = (task) => {
    setFollowUpParent(task);
    setFollowUpForm({
      title: `Follow-up: ${task.title}`,
      description: '',
      priority: task.priority || 'medium'
    });
    setShowFollowUpDialog(true);
  };

  const createFollowUp = async () => {
    if (!followUpParent || !followUpForm.title) return;
    
    try {
      setCreatingFollowUp(true);
      const params = new URLSearchParams({
        title: followUpForm.title,
        description: followUpForm.description,
        priority: followUpForm.priority
      });
      await api.post(`/leadership-tasks/${followUpParent.id}/follow-up?${params.toString()}`);
      toast.success('Follow-up task created');
      setShowFollowUpDialog(false);
      setFollowUpParent(null);
      setFollowUpForm({ title: '', description: '', priority: 'medium' });
      fetchHubData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create follow-up task');
    } finally {
      setCreatingFollowUp(false);
    }
  };

  // AI Agent assignment handlers
  const handleAssignAgent = async (task) => {
    setAssigningToTask(task);
    setSelectedAgent('');
    setAgentRole('contributor');
    setAgentResponsibilities('');
    setShowAgentDialog(true);
    
    // Fetch available agents
    try {
      const response = await api.get('/leadership-ai/available-agents');
      setAvailableAgents(response.data);
    } catch (error) {
      toast.error('Failed to load available AI agents');
    }
  };

  const assignAgent = async () => {
    if (!assigningToTask || !selectedAgent) return;
    
    try {
      setAssigningAgent(true);
      const params = new URLSearchParams({
        agent_id: selectedAgent,
        role: agentRole,
        responsibilities: agentResponsibilities
      });
      await api.post(`/leadership-tasks/${assigningToTask.id}/assign-agent?${params.toString()}`);
      toast.success('AI agent assigned to task');
      setShowAgentDialog(false);
      setAssigningToTask(null);
      fetchHubData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign agent');
    } finally {
      setAssigningAgent(false);
    }
  };

  // Force restart a stalled or paused task
  const handleForceRestart = async (task) => {
    if (!window.confirm(`Are you sure you want to force restart task "${task.title}"? This will clear all current progress and deliverables.`)) {
      return;
    }
    
    try {
      const response = await api.post(`/leadership-tasks/${task.id}/force-restart`);
      toast.success(response.data.message);
      fetchHubData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to force restart task');
    }
  };

  const toggleReasoning = (brain) => {
    setExpandedReasoning(prev => ({ ...prev, [brain]: !prev[brain] }));
  };

  if (loading && hubData.tasks.length === 0) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-violet-600" />
          <p className="text-muted-foreground">Loading Leadership AI Hub...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="leadership-ai-hub-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-violet-600 flex items-center justify-center">
            <Crown className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Leadership AI Hub</h1>
            <p className="text-muted-foreground">View, edit, and execute AI tasks with 3-brain reasoning</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={fetchHubData} disabled={loading} variant="outline">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/20 dark:to-purple-950/20">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-violet-700 dark:text-violet-400">{hubData.stats.total || 0}</p>
            <p className="text-sm text-muted-foreground">Total Tasks</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{hubData.stats.pending || 0}</p>
            <p className="text-sm text-muted-foreground">Pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-amber-600">{hubData.stats.in_progress || 0}</p>
            <p className="text-sm text-muted-foreground">In Progress</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{hubData.stats.completed || 0}</p>
            <p className="text-sm text-muted-foreground">Completed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-600">{hubData.stats.blocked || 0}</p>
            <p className="text-sm text-muted-foreground">Blocked</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-emerald-600">{hubData.stats.with_deliverables || 0}</p>
            <p className="text-sm text-muted-foreground">With Deliverables</p>
          </CardContent>
        </Card>
      </div>

      {/* AI Agent Skills Badges Section */}
      <Card className="border-violet-200 dark:border-violet-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Bot className="h-5 w-5 text-violet-600" />
            AI Agent Skills & Specializations
            <Badge variant="secondary" className="ml-2">
              {availableAgents.length} Active {availableAgents.length === 1 ? 'Agent' : 'Agents'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {availableAgents.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              <Bot className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No AI agents available</p>
              <p className="text-xs">Create agents in the AI Workforce section</p>
            </div>
          ) : (
            <div className="space-y-4">
              {availableAgents.map((agent) => {
                const skillColors = {
                  'task_orchestration': 'bg-violet-100 text-violet-700 border-violet-200',
                  'delegation': 'bg-blue-100 text-blue-700 border-blue-200',
                  'quality_control': 'bg-green-100 text-green-700 border-green-200',
                  'crm_integration': 'bg-orange-100 text-orange-700 border-orange-200',
                  'sales_support': 'bg-emerald-100 text-emerald-700 border-emerald-200',
                  'lead_qualification': 'bg-cyan-100 text-cyan-700 border-cyan-200',
                  'content_creation': 'bg-pink-100 text-pink-700 border-pink-200',
                  'campaign_management': 'bg-purple-100 text-purple-700 border-purple-200',
                  'research': 'bg-indigo-100 text-indigo-700 border-indigo-200',
                  'analysis': 'bg-sky-100 text-sky-700 border-sky-200',
                  'communication': 'bg-amber-100 text-amber-700 border-amber-200',
                  'reporting': 'bg-rose-100 text-rose-700 border-rose-200',
                };
                
                return (
                  <div 
                    key={agent.id} 
                    className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800"
                    data-testid={`agent-skills-${agent.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-[180px]">
                      <div className="p-2 rounded-full bg-violet-100 dark:bg-violet-900">
                        <Crown className="h-5 w-5 text-violet-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{agent.display_name || agent.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {agent.tasks_completed || 0} tasks completed
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 flex-1">
                      {(agent.specializations || []).map((skill, idx) => {
                        const colorClass = skillColors[skill] || 'bg-slate-100 text-slate-700 border-slate-200';
                        const skillLabel = skill.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                        return (
                          <Badge 
                            key={idx} 
                            variant="outline" 
                            className={`text-xs ${colorClass} border`}
                          >
                            {skillLabel}
                          </Badge>
                        );
                      })}
                      {(!agent.specializations || agent.specializations.length === 0) && (
                        <span className="text-xs text-muted-foreground italic">No specializations defined</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 sm:ml-auto">
                      {agent.avg_approval_rate > 0 && (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                          {Math.round(agent.avg_approval_rate * 100)}% approval
                        </Badge>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => {
                          setFilterCategory('');
                          setSearchQuery(agent.display_name || agent.name);
                        }}
                        data-testid={`filter-by-agent-${agent.id}`}
                      >
                        <Search className="h-3 w-3 mr-1" />
                        Find Tasks
                      </Button>
                    </div>
                  </div>
                );
              })}
              
              {/* Category Quick Filters */}
              <div className="pt-3 border-t">
                <p className="text-xs text-muted-foreground mb-2">Quick filter by category:</p>
                <div className="flex flex-wrap gap-2">
                  {['sales', 'marketing', 'operations', 'research', 'communication', 'finance'].map((cat) => (
                    <Button
                      key={cat}
                      variant={filterCategory === cat ? 'default' : 'outline'}
                      size="sm"
                      className="text-xs capitalize"
                      onClick={() => setFilterCategory(filterCategory === cat ? '' : cat)}
                      data-testid={`filter-category-${cat}`}
                    >
                      {cat}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Insights Panel */}
      <AIInsightsPanel />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="search-tasks-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="blocked">Blocked</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priority</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tasks Grid */}
      {filteredTasks.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Crown className="h-12 w-12 mx-auto mb-4 text-violet-300" />
            <h3 className="text-lg font-medium mb-2">No Leadership AI Tasks</h3>
            <p className="text-muted-foreground mb-4">
              Create tasks from the Leadership AI widget on your dashboard
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTasks.map(task => (
            <TaskCard 
              key={task.id} 
              task={task} 
              onView={handleViewTask}
              onEdit={handleEditTask}
              onDelete={handleDeleteTask}
              onExecute={handleExecuteTask}
              onLink={handleLinkTask}
              onFollowUp={handleFollowUp}
              onAssignAgent={handleAssignAgent}
              onForceRestart={handleForceRestart}
            />
          ))}
        </div>
      )}

      {/* Task Detail Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedTask && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="flex items-center gap-2">
                      <Crown className="h-5 w-5 text-violet-600" />
                      {selectedTask.title}
                    </DialogTitle>
                    <DialogDescription className="mt-1">
                      Created {format(new Date(selectedTask.created_at), 'PPp')}
                    </DialogDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(selectedTask.status)}
                    {getPriorityBadge(selectedTask.priority)}
                  </div>
                </div>
              </DialogHeader>

              <Tabs defaultValue="reasoning" className="mt-4">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="reasoning" className="gap-1">
                    <Brain className="h-4 w-4" />
                    AI Reasoning
                  </TabsTrigger>
                  <TabsTrigger value="details" className="gap-1">
                    <FileText className="h-4 w-4" />
                    Details
                  </TabsTrigger>
                  <TabsTrigger value="deliverables" className="gap-1">
                    <Package className="h-4 w-4" />
                    Deliverables
                  </TabsTrigger>
                  <TabsTrigger value="links" className="gap-1">
                    <Link2 className="h-4 w-4" />
                    Links
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="reasoning" className="mt-4">
                  <AIReasoningSection 
                    reasoning={selectedTask.ai_reasoning}
                    expandedReasoning={expandedReasoning}
                    toggleReasoning={toggleReasoning}
                  />
                </TabsContent>

                <TabsContent value="details" className="mt-4">
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium mb-2">Description</h4>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {selectedTask.description}
                      </p>
                    </div>
                    {selectedTask.context && (
                      <div>
                        <h4 className="font-medium mb-2">Additional Context</h4>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                          {selectedTask.context}
                        </p>
                      </div>
                    )}
                    {selectedTask.deliverable_context && (
                      <div>
                        <h4 className="font-medium mb-2">Deliverable Requirements</h4>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                          {selectedTask.deliverable_context}
                        </p>
                      </div>
                    )}
                    {selectedTask.blockers && selectedTask.blockers.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2 flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-red-500" />
                          Blockers
                        </h4>
                        <div className="space-y-2">
                          {selectedTask.blockers.map((blocker, idx) => (
                            <div key={idx} className={`p-3 rounded-lg border ${blocker.resolved ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={blocker.resolved ? 'bg-green-100' : 'bg-red-100'}>
                                  {blocker.blocker_type}
                                </Badge>
                                {blocker.resolved && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                              </div>
                              <p className="text-sm mt-1">{blocker.description}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Bug #21 Fix: Show associated files */}
                    {selectedTask.files && selectedTask.files.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2 flex items-center gap-2">
                          <Paperclip className="h-4 w-4 text-blue-500" />
                          Associated Files ({selectedTask.files.length})
                        </h4>
                        <div className="space-y-2">
                          {selectedTask.files.map((file, idx) => (
                            <div key={file.id || idx} className="p-3 rounded-lg border bg-blue-50 border-blue-200 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <FileText className="h-5 w-5 text-blue-600" />
                                <div>
                                  <p className="text-sm font-medium">{file.original_filename || file.filename}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {file.file_type} • {file.file_size ? `${(file.file_size / 1024).toFixed(1)} KB` : 'Unknown size'}
                                  </p>
                                </div>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  try {
                                    const response = await api.get(`/file-library/${file.id}/download`, { responseType: 'blob' });
                                    const url = window.URL.createObjectURL(new Blob([response.data]));
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = file.original_filename || file.filename;
                                    document.body.appendChild(a);
                                    a.click();
                                    a.remove();
                                    window.URL.revokeObjectURL(url);
                                  } catch (error) {
                                    toast.error('Failed to download file');
                                  }
                                }}
                                data-testid={`download-file-${idx}`}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedTask.file_ids && selectedTask.file_ids.length > 0 && !selectedTask.files && (
                      <div>
                        <h4 className="font-medium mb-2 flex items-center gap-2">
                          <Paperclip className="h-4 w-4 text-blue-500" />
                          Associated Files ({selectedTask.file_ids.length})
                        </h4>
                        <p className="text-sm text-muted-foreground">Files linked but not loaded. Refresh to view.</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="deliverables" className="mt-4">
                  {selectedTask.deliverables && selectedTask.deliverables.length > 0 ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium flex items-center gap-2">
                          <Package className="h-4 w-4" />
                          Generated Files ({selectedTask.deliverables.length})
                        </h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedTask.deliverables.map((deliverable, idx) => {
                          const formatIcons = {
                            'docx': { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Word Document' },
                            'xlsx': { icon: FileSpreadsheet, color: 'text-green-600', bg: 'bg-green-100', label: 'Excel Spreadsheet' },
                            'pptx': { icon: Presentation, color: 'text-orange-600', bg: 'bg-orange-100', label: 'PowerPoint' },
                            'pdf': { icon: File, color: 'text-red-600', bg: 'bg-red-100', label: 'PDF Document' },
                            'markdown': { icon: FileText, color: 'text-slate-600', bg: 'bg-slate-100', label: 'Markdown' },
                            'text': { icon: FileText, color: 'text-slate-600', bg: 'bg-slate-100', label: 'Text' },
                          };
                          const formatInfo = formatIcons[deliverable.format] || formatIcons['text'];
                          const FormatIcon = formatInfo.icon;
                          
                          return (
                            <Card key={idx} className="border-2 hover:border-violet-300 transition-colors">
                              <CardContent className="p-4">
                                <div className="flex items-start gap-3">
                                  <div className={`p-2 rounded-lg ${formatInfo.bg}`}>
                                    <FormatIcon className={`h-6 w-6 ${formatInfo.color}`} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h5 className="font-medium text-sm truncate">
                                      {deliverable.title || `${deliverable.type?.replace('_', ' ')} Output`}
                                    </h5>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {formatInfo.label} • {deliverable.type?.replace('_', ' ')}
                                    </p>
                                    {deliverable.ai_models_used && (
                                      <p className="text-xs text-violet-600 mt-1 flex items-center gap-1">
                                        <Brain className="h-3 w-3" />
                                        3-Brain AI Generated
                                      </p>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Preview of content */}
                                {deliverable.content && (
                                  <div className="mt-3 p-2 bg-slate-50 dark:bg-slate-900 rounded text-xs text-muted-foreground max-h-20 overflow-hidden">
                                    {deliverable.content.substring(0, 200)}...
                                  </div>
                                )}
                                
                                {/* Action buttons */}
                                <div className="flex gap-2 mt-3">
                                  {deliverable.file_path && (
                                    <Button 
                                      variant="default" 
                                      size="sm"
                                      className="flex-1 bg-violet-600 hover:bg-violet-700"
                                      onClick={async () => {
                                        try {
                                          const response = await api.get(`/leadership-ai/deliverables/${deliverable.id}/download`, {
                                            responseType: 'blob'
                                          });
                                          // Get the content type from the response or use a default based on file extension
                                          const contentType = response.headers['content-type'] || 'application/octet-stream';
                                          const blob = new Blob([response.data], { type: contentType });
                                          const url = window.URL.createObjectURL(blob);
                                          const a = document.createElement('a');
                                          a.href = url;
                                          // Ensure proper file extension
                                          let filename = deliverable.file_name || `deliverable_${deliverable.id}`;
                                          if (!filename.includes('.')) {
                                            // Add extension based on content type
                                            if (contentType.includes('pdf')) filename += '.pdf';
                                            else if (contentType.includes('word') || contentType.includes('docx')) filename += '.docx';
                                            else if (contentType.includes('text')) filename += '.txt';
                                            else filename += '.bin';
                                          }
                                          a.download = filename;
                                          document.body.appendChild(a);
                                          a.click();
                                          window.URL.revokeObjectURL(url);
                                          document.body.removeChild(a);
                                        } catch (error) {
                                          console.error('Download error:', error);
                                          toast.error('Failed to download file');
                                        }
                                      }}
                                      data-testid={`download-deliverable-${idx}`}
                                    >
                                      <Download className="h-3 w-3 mr-1" />
                                      Download {formatInfo.label.split(' ')[0]}
                                    </Button>
                                  )}
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => {
                                      // Show full content in modal or copy to clipboard
                                      if (deliverable.content) {
                                        navigator.clipboard.writeText(deliverable.content);
                                        toast.success('Content copied to clipboard');
                                      }
                                    }}
                                  >
                                    <Eye className="h-3 w-3 mr-1" />
                                    Copy Text
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Package className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p>No deliverables generated yet.</p>
                      <p className="text-sm mt-1">Execute this task to generate research reports, spreadsheets, or documents.</p>
                      {selectedTask.status === 'pending' && (
                        <Button 
                          variant="default" 
                          size="sm" 
                          className="mt-4 bg-violet-600 hover:bg-violet-700"
                          onClick={() => {
                            setShowTaskDialog(false);
                            handleExecuteTask(selectedTask);
                          }}
                        >
                          <Rocket className="h-4 w-4 mr-2" />
                          Execute Task Now
                        </Button>
                      )}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="links" className="mt-4">
                  <LinkedEntitiesSection task={selectedTask} onLink={handleLinkTask} />
                </TabsContent>
              </Tabs>

              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setShowTaskDialog(false)}>
                  Close
                </Button>
                <Button variant="outline" onClick={() => { setShowTaskDialog(false); handleEditTask(selectedTask); }}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                {(selectedTask.status === 'pending' || selectedTask.status === 'draft') && (
                  <Button onClick={() => { setShowTaskDialog(false); handleExecuteTask(selectedTask); }} className="bg-violet-600 hover:bg-violet-700">
                    <Rocket className="h-4 w-4 mr-2" />
                    Execute with AI
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edit Task
            </DialogTitle>
            <DialogDescription>
              Update the task details below.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Title</Label>
              <Input
                id="edit-title"
                value={editForm.title}
                onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Task title"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={editForm.description}
                onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Task description"
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-context">Additional Context</Label>
              <Textarea
                id="edit-context"
                value={editForm.context}
                onChange={(e) => setEditForm(prev => ({ ...prev, context: e.target.value }))}
                placeholder="Any additional context for the AI"
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={editForm.priority} onValueChange={(v) => setEditForm(prev => ({ ...prev, priority: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="blocked">Blocked</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={saving || !editForm.title}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deletingTask?.title}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" disabled={deleting}>
              {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Execute Confirmation Dialog */}
      <Dialog open={showExecuteDialog} onOpenChange={setShowExecuteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Rocket className="h-5 w-5 text-violet-600" />
              Execute Task with AI
            </DialogTitle>
            <DialogDescription>
              This will run the task through the 3-brain AI system (Left Brain, Right Brain, Cortex) to generate reasoning and deliverables.
            </DialogDescription>
          </DialogHeader>
          
          {executingTask && (
            <div className="py-4">
              <div className="p-4 bg-violet-50 dark:bg-violet-950/20 rounded-lg mb-4">
                <h4 className="font-medium">{executingTask.title}</h4>
                <p className="text-sm text-muted-foreground mt-1">{executingTask.description}</p>
              </div>
              
              {executing && (
                <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <Loader2 className="h-5 w-5 animate-spin text-violet-600" />
                  <div>
                    <p className="font-medium">Processing...</p>
                    <p className="text-sm text-muted-foreground">{executionProgress}</p>
                  </div>
                </div>
              )}
              
              {!executing && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Brain className="h-4 w-4 text-blue-600" />
                    <span>Left Brain (GPT-4o) - Analytical reasoning</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Sparkles className="h-4 w-4 text-purple-600" />
                    <span>Right Brain (Claude Sonnet 4.5) - Creative reasoning</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Zap className="h-4 w-4 text-emerald-600" />
                    <span>Cortex (Gemini 3 Pro) - Synthesis &amp; decision</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExecuteDialog(false)} disabled={executing}>
              Cancel
            </Button>
            <Button onClick={confirmExecute} disabled={executing} className="bg-violet-600 hover:bg-violet-700">
              {executing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Executing...
                </>
              ) : (
                <>
                  <Rocket className="h-4 w-4 mr-2" />
                  Execute Now
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Link Task to Entity
            </DialogTitle>
            <DialogDescription>
              Link this task to a campaign, contact, or other CRM entity for cross-module visibility.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Entity Type</Label>
              <Select value={linkEntityType} onValueChange={setLinkEntityType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select entity type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="campaign">Marketing Campaign</SelectItem>
                  <SelectItem value="sales_campaign">Sales Campaign</SelectItem>
                  <SelectItem value="market_research">Market Research</SelectItem>
                  <SelectItem value="contact">Contact</SelectItem>
                  <SelectItem value="company">Company</SelectItem>
                  <SelectItem value="deal">Deal</SelectItem>
                  <SelectItem value="lead">Lead/Prospect</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Entity ID</Label>
              <Input
                placeholder="Enter entity ID or select from list"
                value={linkEntityId}
                onChange={(e) => setLinkEntityId(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                You can find the ID in the URL when viewing the entity
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkDialog(false)}>Cancel</Button>
            <Button onClick={submitLink} disabled={!linkEntityType || !linkEntityId}>
              <Link2 className="h-4 w-4 mr-2" />
              Link Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Follow-up Task Dialog */}
      <Dialog open={showFollowUpDialog} onOpenChange={setShowFollowUpDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitBranch className="h-5 w-5 text-violet-600" />
              Create Follow-up Task
            </DialogTitle>
            <DialogDescription>
              Create a follow-up task linked to &quot;{followUpParent?.title}&quot;
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="followup-title">Title</Label>
              <Input
                id="followup-title"
                value={followUpForm.title}
                onChange={(e) => setFollowUpForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Follow-up task title"
                data-testid="followup-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="followup-description">Description</Label>
              <Textarea
                id="followup-description"
                value={followUpForm.description}
                onChange={(e) => setFollowUpForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What needs to be done in this follow-up?"
                rows={3}
                data-testid="followup-description-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select 
                value={followUpForm.priority} 
                onValueChange={(v) => setFollowUpForm(prev => ({ ...prev, priority: v }))}
              >
                <SelectTrigger data-testid="followup-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {followUpParent && (
              <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border">
                <p className="text-xs text-muted-foreground mb-1">Parent Task</p>
                <p className="text-sm font-medium">{followUpParent.title}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFollowUpDialog(false)} disabled={creatingFollowUp}>
              Cancel
            </Button>
            <Button 
              onClick={createFollowUp} 
              disabled={creatingFollowUp || !followUpForm.title}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="create-followup-btn"
            >
              {creatingFollowUp ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <GitBranch className="h-4 w-4 mr-2" />
                  Create Follow-up
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Agent Assignment Dialog */}
      <Dialog open={showAgentDialog} onOpenChange={setShowAgentDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-indigo-600" />
              Assign AI Staff
            </DialogTitle>
            <DialogDescription>
              Assign an AI agent to support &quot;{assigningToTask?.title}&quot;
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select AI Agent</Label>
              <Select 
                value={selectedAgent} 
                onValueChange={setSelectedAgent}
              >
                <SelectTrigger data-testid="agent-select">
                  <SelectValue placeholder="Choose an AI agent" />
                </SelectTrigger>
                <SelectContent>
                  {availableAgents.map(agent => (
                    <SelectItem key={agent.id} value={agent.id}>
                      <div className="flex items-center gap-2">
                        <Bot className="h-4 w-4" />
                        <span>{agent.name}</span>
                        {agent.role_title && (
                          <Badge variant="outline" className="text-xs ml-2">{agent.role_title}</Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                  {availableAgents.length === 0 && (
                    <SelectItem value="__none__" disabled>No AI agents available</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Role in Task</Label>
              <Select 
                value={agentRole} 
                onValueChange={setAgentRole}
              >
                <SelectTrigger data-testid="agent-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lead">Lead - Primary responsibility</SelectItem>
                  <SelectItem value="contributor">Contributor - Support role</SelectItem>
                  <SelectItem value="reviewer">Reviewer - Quality check</SelectItem>
                  <SelectItem value="specialist">Specialist - Domain expert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="agent-responsibilities">Responsibilities (optional)</Label>
              <Textarea
                id="agent-responsibilities"
                value={agentResponsibilities}
                onChange={(e) => setAgentResponsibilities(e.target.value)}
                placeholder="Describe what this agent should focus on..."
                rows={2}
                data-testid="agent-responsibilities-input"
              />
            </div>

            {assigningToTask?.assigned_ai_agents && assigningToTask.assigned_ai_agents.length > 0 && (
              <div className="space-y-2">
                <Label>Currently Assigned</Label>
                <div className="space-y-2">
                  {assigningToTask.assigned_ai_agents.map((agent, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-indigo-50 dark:bg-indigo-950/20 rounded">
                      <div className="flex items-center gap-2">
                        <Bot className="h-4 w-4 text-indigo-600" />
                        <span className="text-sm">{agent.name || agent.agent_id}</span>
                        {agent.role && <Badge variant="outline" className="text-xs">{agent.role}</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAgentDialog(false)} disabled={assigningAgent}>
              Cancel
            </Button>
            <Button 
              onClick={assignAgent} 
              disabled={assigningAgent || !selectedAgent}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="assign-agent-btn"
            >
              {assigningAgent ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <Users className="h-4 w-4 mr-2" />
                  Assign Agent
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Coaching Panel Section */}
      <div className="mt-6">
        <AICoachingPanel />
      </div>
    </div>
  );
}
